import React from 'react';
import { motion } from 'framer-motion';
import { Crown, CheckCircle2, ArrowRight, MapPin, ShieldCheck, Truck, Star } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { CartSidebar } from '../components/CartSidebar';
import { CartItem, Page } from '../App';
const values = [{
  icon: CheckCircle2,
  text: 'Quality you can trust'
}, {
  icon: Star,
  text: 'Designs that elevate your lifestyle'
}, {
  icon: Truck,
  text: 'Fair and transparent logistics pricing'
}, {
  icon: ShieldCheck,
  text: 'Secure, seamless online payments'
}, {
  icon: MapPin,
  text: 'Nationwide delivery within Nigeria'
}];
interface PageProps {
  onAddToCart: (product: Omit<CartItem, 'quantity'>) => void;
  onNavigate: (page: Page) => void;
  cartItems: CartItem[];
  cartCount: number;
  isCartOpen: boolean;
  onOpenCart: () => void;
  onCloseCart: () => void;
  onUpdateQuantity: (id: string, delta: number) => void;
  onRemoveItem: (id: string) => void;
}
export function AboutPage({
  onNavigate,
  cartItems,
  cartCount,
  isCartOpen,
  onOpenCart,
  onCloseCart,
  onUpdateQuantity,
  onRemoveItem
}: PageProps) {
  return <div className="bg-[#0F0F0F] min-h-screen text-white" data-id="element-279">
      <Navbar onOpenCart={onOpenCart} cartCount={cartCount} onNavigate={onNavigate} data-id="element-280" />

      {/* Hero */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 overflow-hidden" data-id="element-281">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[#D4AF37] rounded-full mix-blend-screen filter blur-[150px] opacity-[0.06]" data-id="element-282" />
        <div className="max-w-4xl mx-auto text-center relative z-10" data-id="element-283">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8
        }} data-id="element-284">
            <div className="flex justify-center mb-6" data-id="element-285">
              <div className="p-4 bg-[#D4AF37]/10 rounded-2xl border border-[#D4AF37]/20" data-id="element-286">
                <Crown className="w-10 h-10 text-[#D4AF37]" data-id="element-287" />
              </div>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold font-serif text-white mb-6" data-id="element-288">
              About <span className="text-[#D4AF37]" data-id="element-289">Luxe Collections</span>
            </h1>
            <div className="w-20 h-1 bg-gradient-to-r from-[#D4AF37] to-transparent mx-auto mb-8" data-id="element-290" />
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <section className="px-4 sm:px-6 lg:px-8 pb-20" data-id="element-291">
        <div className="max-w-3xl mx-auto" data-id="element-292">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="space-y-6 text-gray-300 text-lg leading-relaxed" data-id="element-293">
            <p data-id="element-294">
              Luxe Collections is a Nigerian eCommerce brand dedicated to
              bringing premium accessories and quality home appliances to modern
              homes across Nigeria.
            </p>
            <p data-id="element-295">
              Based in{' '}
              <span className="text-[#D4AF37] font-medium" data-id="element-296">Benin City</span>, we
              serve customers nationwide with a simple promise: refined
              products, transparent pricing, and reliable delivery.
            </p>
            <p data-id="element-297">
              We believe that everyday essentials can be both stylish and
              functional. That the items you use daily should reflect your
              standards. And that great service should be part of the experience
              — not an afterthought.
            </p>
          </motion.div>

          {/* Values */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.2
        }} className="mt-12 mb-12" data-id="element-298">
            <h2 className="text-2xl font-bold font-serif text-white mb-6" data-id="element-299">
              Our collections are carefully curated to ensure:
            </h2>
            <div className="space-y-4" data-id="element-300">
              {values.map((value, i) => <motion.div key={i} initial={{
              opacity: 0,
              x: -20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: i * 0.1
            }} className="flex items-center gap-4 p-4 glass-panel rounded-xl border border-white/5" data-id="element-301">
                  <div className="p-2 bg-[#D4AF37]/10 rounded-lg flex-shrink-0" data-id="element-302">
                    <value.icon className="w-5 h-5 text-[#D4AF37]" data-id="element-303" />
                  </div>
                  <span className="text-gray-200 font-medium" data-id="element-304">
                    {value.text}
                  </span>
                </motion.div>)}
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="space-y-6 text-gray-300 text-lg leading-relaxed" data-id="element-305">
            <p data-id="element-306">
              At Luxe Collections, we combine technology, thoughtful logistics,
              and premium sourcing to create a shopping experience that feels
              modern, efficient, and dependable.
            </p>
          </motion.div>

          {/* Closing Statement */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.3
        }} className="mt-12 p-8 glass-panel rounded-2xl border border-[#D4AF37]/20 text-center" data-id="element-307">
            <p className="text-gray-400 text-lg mb-2" data-id="element-308">
              Because luxury isn't about extravagance.
            </p>
            <p className="text-2xl font-bold font-serif text-[#D4AF37]" data-id="element-309">
              It's about living well — intentionally.
            </p>
          </motion.div>

          {/* CTA */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="mt-12 flex flex-col sm:flex-row gap-4 justify-center" data-id="element-310">
            <button onClick={() => onNavigate('fashion')} className="flex items-center justify-center gap-2 px-8 py-4 bg-[#D4AF37] text-black font-bold rounded-full hover:bg-[#c4a030] transition-all" data-id="element-311">
              Shop Now <ArrowRight className="w-5 h-5" data-id="element-312" />
            </button>
            <button onClick={() => onNavigate('track-order')} className="flex items-center justify-center gap-2 px-8 py-4 border border-[#D4AF37] text-[#D4AF37] font-medium rounded-full hover:bg-[#D4AF37]/10 transition-all" data-id="element-313">
              Track Your Order
            </button>
          </motion.div>
        </div>
      </section>

      <Footer onNavigate={onNavigate} data-id="element-314" />
      <CartSidebar isOpen={isCartOpen} onClose={onCloseCart} items={cartItems} onUpdateQuantity={onUpdateQuantity} onRemoveItem={onRemoveItem} onNavigate={onNavigate} data-id="element-315" />
    </div>;
}